import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function PaymentResult() {
  const navigate = useNavigate();
  const location = useLocation();
  const { checkAuth } = useAuth();
  const [state, setState] = useState("checking"); // checking | success | failed | cancel
  const attempts = useRef(0);

  const isCancel = location.pathname.includes("cancel");

  useEffect(() => {
    if (isCancel) { setState("cancel"); return; }
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("failed"); return; }

    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await apiClient.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await checkAuth();
          setState("success");
          return;
        }
        if (data.status === "expired" || attempts.current > 12) { setState("failed"); return; }
      } catch (e) {
        if (attempts.current > 12) { setState("failed"); return; }
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [isCancel, location.search, checkAuth]);

  return (
    <div className="min-h-screen bg-[#0A0F0D] text-white flex items-center justify-center px-6">
      <div className="max-w-sm w-full text-center animate-float-up">
        {state === "checking" && (
          <>
            <Loader2 className="h-12 w-12 text-lime-400 animate-spin mx-auto mb-5" />
            <h1 className="font-display text-2xl font-bold mb-2">Validation du paiement…</h1>
            <p className="text-white/50 text-sm">Un instant, on confirme ton abonnement.</p>
          </>
        )}
        {state === "success" && (
          <>
            <div className="h-16 w-16 rounded-full bg-lime-400/15 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="h-9 w-9 text-lime-400" />
            </div>
            <h1 data-testid="payment-success-title" className="font-display text-2xl font-bold mb-2">Bienvenue ! 🎉</h1>
            <p className="text-white/60 text-sm mb-6">Ton abonnement est actif. Tu peux générer ton programme personnalisé.</p>
            <button data-testid="goto-app-button" onClick={() => navigate("/app")}
              className="w-full bg-lime-400 text-black font-extrabold py-3.5 rounded-xl hover:bg-[#b0e600] active:scale-95 transition-all">
              Générer mon programme
            </button>
          </>
        )}
        {(state === "failed" || state === "cancel") && (
          <>
            <div className="h-16 w-16 rounded-full bg-red-500/15 flex items-center justify-center mx-auto mb-5">
              <XCircle className="h-9 w-9 text-red-400" />
            </div>
            <h1 className="font-display text-2xl font-bold mb-2">{state === "cancel" ? "Paiement annulé" : "Paiement non confirmé"}</h1>
            <p className="text-white/60 text-sm mb-6">Aucun montant n'a été débité. Tu peux réessayer quand tu veux.</p>
            <button onClick={() => navigate("/pricing")}
              className="w-full bg-lime-400 text-black font-extrabold py-3.5 rounded-xl hover:bg-[#b0e600] active:scale-95 transition-all">
              Revenir aux formules
            </button>
          </>
        )}
      </div>
    </div>
  );
}
